import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AiChatService } from '@qtek/libs/store';

@Component({
  selector: 'qt-ai-chat-feedback',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatButtonModule],
  template: `
    @if (!submitted()) {
      <div class="qt-feedback">
        <span class="qt-feedback__label"
          >Was this helpful? Rate Allo's response</span
        >
        <div class="qt-feedback__icons">
          <button
            class="qt-feedback__btn"
            [class.qt-feedback__btn--selected]="rating() === 'positive'"
            type="button"
            aria-label="Thumbs up"
            (click)="selectRating('positive')">
            <mat-icon>thumb_up</mat-icon>
          </button>
          <button
            class="qt-feedback__btn"
            [class.qt-feedback__btn--selected]="rating() === 'negative'"
            type="button"
            aria-label="Thumbs down"
            (click)="selectRating('negative')">
            <mat-icon>thumb_down</mat-icon>
          </button>
          <button
            class="qt-feedback__btn"
            [class.qt-feedback__btn--copied]="copied()"
            type="button"
            aria-label="Copy message"
            (click)="copyMessage()">
            <mat-icon>{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>

        @if (rating()) {
          <div class="qt-feedback__comment-area">
            <textarea
              class="qt-feedback__textarea"
              placeholder="What did you like or dislike about this response?"
              [ngModel]="comment()"
              (ngModelChange)="comment.set($event)"
              maxlength="500"
              rows="3">
            </textarea>
            <button
              class="qt-feedback__submit"
              mat-flat-button
              type="button"
              [disabled]="submitting()"
              (click)="submit()">
              Submit
            </button>
          </div>
        }
      </div>
    } @else {
      <div class="qt-feedback qt-feedback--done">
        <span class="qt-feedback__confirm">Thank you for your feedback!</span>
      </div>
    }
  `,
  styles: [
    `
      .qt-feedback {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 8px;
        margin-bottom: 1.5rem;
      }

      .qt-feedback__label {
        font-size: 0.75rem;
        color: var(--app-ai-chat-muted-text, #888);
      }

      .qt-feedback__icons {
        display: flex;
        gap: 8px;
      }

      .qt-feedback__btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        color: var(--app-ai-chat-muted-text, #aaa);
        display: flex;
        align-items: center;
        transition: color 0.15s;
      }

      .qt-feedback__btn:hover {
        color: var(--app-ai-chat-text, #333);
      }

      .qt-feedback__btn--selected {
        color: #f9423a;
      }

      .qt-feedback__btn--copied {
        color: #16a34a;
      }

      .qt-feedback__btn mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      .qt-feedback__comment-area {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .qt-feedback__textarea {
        width: 100%;
        resize: vertical;
        border: 1px solid var(--app-ai-chat-border, #ddd);
        border-radius: 8px;
        padding: 8px 10px;
        font-size: 0.875rem;
        font-family: inherit;
        background: var(--app-ai-chat-input-bg, transparent);
        color: var(--app-ai-chat-text, inherit);
        box-sizing: border-box;
      }

      .qt-feedback__textarea:focus {
        outline: none;
        border-color: #f9423a;
      }

      .qt-feedback__submit {
        align-self: flex-start;
        background-color: #f9423a !important;
        color: #fff !important;
      }

      .qt-feedback--done .qt-feedback__confirm {
        font-size: 0.8125rem;
        color: var(--app-ai-chat-muted-text, #888);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatFeedbackComponent {
  readonly chatId = input.required<string>();
  readonly ts = input.required<number>();
  readonly messageText = input('');

  readonly rating = signal<'positive' | 'negative' | null>(null);
  readonly comment = signal('');
  readonly submitted = signal(false);
  readonly submitting = signal(false);
  readonly copied = signal(false);

  private readonly chatService = inject(AiChatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private copyTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.copyTimeout) clearTimeout(this.copyTimeout);
    });
  }

  selectRating(r: 'positive' | 'negative'): void {
    if (this.submitted()) return;
    this.rating.set(r);
  }

  submit(): void {
    const currentRating = this.rating();
    if (this.submitting() || this.submitted() || !currentRating) return;

    this.submitting.set(true);
    const messageId = `${this.chatId()}.${this.ts()}`;
    const body: {
      messageId: string;
      rating: 'positive' | 'negative';
      comment?: string;
    } = {
      messageId,
      rating: currentRating,
    };
    const trimmedComment = this.comment().trim();
    if (trimmedComment) {
      body.comment = trimmedComment;
    }

    this.chatService.submitFeedback(body).subscribe({
      next: () => {
        this.submitted.set(true);
        this.submitting.set(false);
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('Failed to submit feedback', err);
        this.submitting.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  copyMessage(): void {
    const plain = this.extractPlainText(this.messageText());
    navigator.clipboard.writeText(plain).then(
      () => {
        this.copied.set(true);
        this.cdr.markForCheck();
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.copied.set(false);
          this.cdr.markForCheck();
        }, 2000);
      },
      () => {
        /* clipboard write failed — no visual change */
      }
    );
  }

  private extractPlainText(text: string): string {
    let result = text;

    // For action cards: keep only text before the first <action-card>
    const actionCardIndex = result.search(/<action-card>/i);
    if (actionCardIndex !== -1) {
      result = result.substring(0, actionCardIndex);
    }

    // Remove thinking blocks
    result = result.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '');

    // Remove fenced action-list JSON blocks
    result = result.replace(/```json\s*\{[\s\S]*?\}\s*```/gi, '');

    // Remove inline action-list JSON
    result = result.replace(
      /\{\s*"type"\s*:\s*"action_list"\s*,[\s\S]*?\}/g,
      ''
    );

    // Strip markdown formatting while preserving content and line breaks
    result = result
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/\*(?!\s)([^*]+?)\*/g, '$1')
      .replace(/_(?!\s)([^_]+?)_/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    return result.trim();
  }
}
